import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BoardFilters } from './board-filters';
import { ChapterColumn } from './chapter-column';
import { SceneCard } from './scene-card';
import { SortableChapterColumn } from './sortable-chapter-column';

import { useCreateChapter } from '@/features/chapters/hooks/use-mutation-hooks';
import { useDoubleHorizontalScroll } from '@/hooks/useDoubleHorizontalScroll';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { EntitySummary, NarrativeSummary, TextSummary } from '@/lib/types';

export function ChapterBoard() {
  const { t } = useTranslation();
  const {
    novel, firestore,
    batchSaveItems, updateNarrativeSummary,
    novelDao
  } = useNovelRepository();
  const isLoading = !novel;
  const { createChapter } = useCreateChapter();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterPov, setFilterPov] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [activeSceneId, setActiveSceneId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<'scene' | 'chapter' | null>(null);
  const [localScenesByChapter, setLocalScenesByChapter] = useState<Record<string, NarrativeSummary[]>>({});
  const [localChapters, setLocalChapters] = useState<NarrativeSummary[]>([]);

  const chapters = useMemo(() => {
    return (Object.values(novel?.summaries?.chapters || {}) as NarrativeSummary[])
      .filter((ch) => !ch.isArchive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.updatedAt ?? '').localeCompare(b.updatedAt ?? ''));
  }, [novel]);

  const scenes = useMemo(() => {
    return (Object.values(novel?.summaries?.scenes || {}) as NarrativeSummary[])
      .filter(s => !s.isArchive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.updatedAt ?? '').localeCompare(b.updatedAt ?? ''));
  }, [novel]);

  const characters = useMemo(() => (novel?.summaries?.characters || {}) as Record<string, EntitySummary>, [novel]);
  const locations = useMemo(() => (novel?.summaries?.locations || {}) as Record<string, EntitySummary>, [novel]);
  const organizations = useMemo(() => (novel?.summaries?.organizations || {}) as Record<string, EntitySummary>, [novel]);
  const eventsMeta = useMemo(() => (novel?.summaries?.events || {}) as Record<string, EntitySummary>, [novel]);
  const contents = useMemo(() => (novel?.summaries?.contents || {}) as Record<string, TextSummary>, [novel]);

  // Initialize with ALL scenes to ensure we don't lose data during drag/drop with filters
  useEffect(() => {
    const grouped: Record<string, NarrativeSummary[]> = {};
    chapters.forEach(ch => grouped[ch.id] = []);
    grouped['unassigned'] = [];

    scenes.forEach(scene => {
      const chId = scene.chapterId || 'unassigned';
      if (!grouped[chId]) grouped[chId] = [];
      grouped[chId].push(scene);
    });

    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const itemA = a as NarrativeSummary;
        const itemB = b as NarrativeSummary;
        const orderA = itemA.order ?? 0;
        const orderB = itemB.order ?? 0;
        if (orderA !== orderB) return orderA - orderB;

        return (itemA.updatedAt ?? '').localeCompare(itemB.updatedAt ?? '');
      });
    });

    setLocalScenesByChapter(grouped);
  }, [chapters, scenes]);

  useEffect(() => {
    setLocalChapters(chapters);
  }, [chapters]);

  const {
    topScrollRef,
    contentScrollRef,
    contentWidth,
    handleTopScroll,
    handleContentScroll,
  } = useDoubleHorizontalScroll([localChapters, localScenesByChapter]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findContainer = (id: string) => {
    if (id in localScenesByChapter) return id;
    if (id === 'unassigned') return 'unassigned';
    return Object.keys(localScenesByChapter).find(key =>
      localScenesByChapter[key].some(it => it.id === id)
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveSceneId(active.id as string);
    setActiveType(active.data.current?.type === 'container' ? 'chapter' : 'scene');
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeType === 'chapter') return;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setLocalScenesByChapter(prev => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];
      const activeIndex = activeItems.findIndex(i => i.id === activeId);
      const overIndex = overItems.findIndex(i => i.id === overId);

      let newIndex;
      if (overId in prev) {
        newIndex = overItems.length;
      } else {
        newIndex = overIndex >= 0 ? overIndex : overItems.length;
      }

      return {
        ...prev,
        [activeContainer]: activeItems.filter(i => i.id !== activeId),
        [overContainer]: [
          ...overItems.slice(0, newIndex),
          activeItems[activeIndex],
          ...overItems.slice(newIndex)
        ]
      };
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !firestore || !novel.id) {
      setActiveSceneId(null);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeType === 'chapter') {
      if (activeId !== overId) {
        const oldIndex = localChapters.findIndex(ch => ch.id === activeId);
        const newIndex = localChapters.findIndex(ch => ch.id === overId);
        const newChapters = arrayMove(localChapters, oldIndex, newIndex);

        setLocalChapters(newChapters);

        // Persist
        const now = new Date().toISOString();
        const updates = newChapters.map((ch, idx) => ({
          ...ch,
          order: idx,
          updatedAt: now
        }));

        await batchSaveItems('narrative', 'chapters', updates);

        const summaryUpdates: Record<string, NarrativeSummary> = {};
        updates.forEach(ch => {
          summaryUpdates[ch.id] = ch;
        });
        if (novelDao) {
          await novelDao.batchUpdateSummaries(novel.ownerId, novel, 'narrative', 'chapters', summaryUpdates);
        }
      }
      setActiveSceneId(null);
      setActiveType(null);
      return;
    }

    const activeContainerLocal = findContainer(activeId);
    const overContainerLocal = findContainer(overId);

    if (!activeContainerLocal || !overContainerLocal) {
      setActiveSceneId(null);
      setActiveType(null);
      return;
    }

    // Determine the original container to detect cross-chapter moves reliably
    const originalScene = scenes.find(s => s.id === activeId);
    const originalContainer = originalScene ? (originalScene.chapterId || 'unassigned') : activeContainerLocal;

    if (activeId !== overId || originalContainer !== overContainerLocal) {
      const activeItems = localScenesByChapter[activeContainerLocal];
      const overItems = localScenesByChapter[overContainerLocal];
      const activeIndex = activeItems.findIndex(i => i.id === activeId);
      const overIndex = overItems.findIndex(i => i.id === overId);

      let finalScenes = [...overItems];
      if (activeContainerLocal === overContainerLocal) {
        finalScenes = arrayMove(overItems, activeIndex, overIndex);
      }

      setLocalScenesByChapter(prev => ({
        ...prev,
        [overContainerLocal]: finalScenes
      }));

      // Persist changes
      const now = new Date().toISOString();

      // Update ALL scenes in the target chapter with new orders
      const targetUpdates: NarrativeSummary[] = finalScenes.map((s, idx) => ({
        ...s,
        chapterId: overContainerLocal === 'unassigned' ? '' : overContainerLocal,
        order: idx,
        updatedAt: now
      }));

      let allUpdates: NarrativeSummary[] = [...targetUpdates];

      // If moved across chapters, update the source chapter's order to remove gaps
      if (originalContainer !== overContainerLocal) {
        const sourceItems = localScenesByChapter[originalContainer];
        // sourceItems doesn't have the active item because handleDragOver removed it
        if (sourceItems) {
          const sourceUpdates: NarrativeSummary[] = sourceItems.map((s, idx) => ({
            ...s,
            order: idx,
            updatedAt: now
          }));
          allUpdates = [...allUpdates, ...sourceUpdates];
        }
      }

      await batchSaveItems('narrative', 'scenes', allUpdates);

      const summaryUpdates: Record<string, NarrativeSummary> = {};
      allUpdates.forEach(s => {
        summaryUpdates[s.id] = s;
      });
      if (novelDao) {
        await novelDao.batchUpdateSummaries(novel.ownerId, novel, 'narrative', 'scenes', summaryUpdates);
      }
    }

    setActiveSceneId(null);
    setActiveType(null);
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;

  // Filter helper for columns
  const filterFn = (scene: NarrativeSummary): boolean => {
    const matchesSearch = scene.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (scene.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesPov = filterPov === 'all' || scene.povCharacterId === filterPov;
    const matchesLocation = filterLocation === 'all' || (scene.locationIds?.includes(filterLocation) ?? false);
    const matchesStatus = filterStatus === 'all' || scene.status?.phase === filterStatus;
    return !!(matchesSearch && matchesPov && matchesLocation && matchesStatus);
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <BoardFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        filterPov={filterPov} setFilterPov={setFilterPov}
        filterLocation={filterLocation} setFilterLocation={setFilterLocation}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        characters={characters} locations={locations}
      />

      {/* Top Horizontal Scrollbar */}
      <Box
        ref={topScrollRef}
        onScroll={handleTopScroll}
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 9,
          bgcolor: 'background.default',
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          pt: 1,
          pb: 1,
          mb: 2,
          '&::-webkit-scrollbar': {
            height: 8,
          },
        }}
      >
        <Box sx={{ width: contentWidth, pt: '1px', lineHeight: '1px' }}>&nbsp;</Box>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <Box
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          sx={{ flexGrow: 1, overflowX: 'auto', display: 'flex', p: 0, gap: 2, alignItems: 'flex-start' }}
        >
          <SortableContext items={localChapters.map(ch => ch.id)} strategy={horizontalListSortingStrategy}>
            {localChapters.map((ch, idx) => (
              <SortableChapterColumn
                key={ch.id}
                chapter={ch}
                scenes={localScenesByChapter[ch.id] || []}
                characters={characters}
                locations={locations}
                organizations={organizations}
                events={eventsMeta}
                contents={contents}
                index={idx + 1}
                isEditing={editingChapterId === ch.id}
                setEditingId={setEditingChapterId}
                filterFn={filterFn}
              />
            ))}
          </SortableContext>

          <Box onClick={async () => { const id = await createChapter(); if (id) setEditingChapterId(id); }} sx={{ minWidth: 320, width: 320, height: '100px', mt: 6, borderRadius: 3, border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'text.secondary', opacity: 0.6, '&:hover': { opacity: 1, bgcolor: 'action.hover', borderColor: 'primary.main', color: 'primary.main' } }}>
            <Stack direction="row" spacing={1} alignItems="center"><AddIcon /><Typography variant="button" sx={{ fontWeight: 700 }}>{t('editor.new_chapter')}</Typography></Stack>
          </Box>

          <Box
            sx={{
              width: (localScenesByChapter['unassigned']?.length > 0 || scenes.some(s => !s.chapterId)) ? 320 : 0,
              minWidth: (localScenesByChapter['unassigned']?.length > 0 || scenes.some(s => !s.chapterId)) ? 320 : 0,
              opacity: (localScenesByChapter['unassigned']?.length > 0 || scenes.some(s => !s.chapterId)) ? 1 : 0,
              marginLeft: (localScenesByChapter['unassigned']?.length > 0 || scenes.some(s => !s.chapterId)) ? '0px' : '-16px',
              overflow: 'hidden',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: (localScenesByChapter['unassigned']?.length > 0 || scenes.some(s => !s.chapterId)) ? 'auto' : 'none',
            }}
          >
            <ChapterColumn
              chapter={{ id: 'unassigned', name: t('entity_list.scenes.unassigned'), order: 9999, updatedAt: '' }}
              scenes={localScenesByChapter['unassigned'] || []}
              characters={characters}
              locations={locations}
              organizations={organizations}
              events={eventsMeta}
              contents={contents}
              index={0}
              isEditing={false}
              setEditingId={() => { }}
              filterFn={filterFn}
            />
          </Box>

          <DragOverlay>
            {activeType === 'scene' && activeSceneId ? (
              <Box sx={{ width: 320 }}>
                <SceneCard
                  scene={scenes.find(s => s.id === activeSceneId)!}
                  characters={characters}
                  locations={locations}
                  organizations={organizations}
                  events={eventsMeta}
                  contents={contents}
                  isDraggingOverlay
                />
              </Box>
            ) : activeType === 'chapter' && activeSceneId ? (
              <Box sx={{ width: 320, opacity: 0.8 }}>
                <ChapterColumn
                  chapter={localChapters.find(ch => ch.id === activeSceneId)!}
                  scenes={localScenesByChapter[activeSceneId] || []}
                  characters={characters}
                  locations={locations}
                  organizations={organizations}
                  events={eventsMeta}
                  contents={contents}
                  index={localChapters.findIndex(ch => ch.id === activeSceneId) + 1}
                  isEditing={false}
                  setEditingId={() => { }}
                  filterFn={filterFn}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </Box>
      </DndContext>
    </Box>
  );
}
